// One coachmark card of the first-run tour. Presentational only —
// the OnboardingTour orchestrator decides which beat renders and
// where. Follows the modal-panel recipe (zinc-900 fill, zinc-800
// hairline, rounded-md, shadow-2xl) at popover scale, with the
// display-eyebrow voice for the step counter.

type Props = {
  /** Step counter + name, e.g. "first edit · 1 of 3". */
  eyebrow: string;
  body: React.ReactNode;
  /** Where the card sits inside the editor column:
   *  'editor' — bottom-right, next to the code being edited;
   *  'agent'  — top-right, arrowed toward the ChatPanel rail. */
  placement: 'editor' | 'agent';
  onDismiss: () => void;
  /** Optional advance affordance (NEXT, or the suggested prompt). */
  action?: { label: string; onClick: () => void };
};

export function Coachmark({
  eyebrow,
  body,
  placement,
  onDismiss,
  action,
}: Props) {
  const position =
    placement === 'agent' ? 'top-6 right-3' : 'bottom-6 right-6';
  return (
    <div
      role="note"
      aria-label={eyebrow}
      className={`coachmark-panel absolute ${position} z-30 w-[300px] rounded-md border border-zinc-800 bg-zinc-900 p-4 shadow-2xl`}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 rounded-full bg-yellow-300 shadow-[0_0_8px_rgba(253,224,71,0.6)]"
        />
        <span className="flex-1 font-display text-[9px] uppercase tracking-[0.25em] text-zinc-500">
          {eyebrow}
        </span>
        {placement === 'agent' && (
          <span aria-hidden="true" className="text-[13px] text-yellow-300">
            →
          </span>
        )}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss tour"
          title="Dismiss the tour (Esc)"
          className="px-1 font-display text-[11px] leading-none text-zinc-600 transition-colors hover:text-zinc-300"
        >
          ✕
        </button>
      </div>
      <div className="mt-2 font-body text-[12px] leading-[1.55] text-zinc-300">
        {body}
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="mt-3 w-full rounded-sm border border-yellow-300/70 bg-yellow-300/10 px-3 py-1.5 text-left font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-yellow-200 transition-colors hover:bg-yellow-300/20"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
